import { Injectable, signal, effect, Injector, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { PermissionEnum } from '@app/generated/graphql';
import {
  INavCategory,
  INavItem,
  INavList,
  INavigationType,
} from '@app/shared/global-layout/interfaces/navigation.interface';
import { filter, map } from 'rxjs/operators';
import { toSignal } from '@angular/core/rxjs-interop';

@Injectable({
  providedIn: 'root',
})
export class NavService {
  private router = inject(Router);
  private injector = inject(Injector);

  static readonly TOGGLE_STATE_CACHE_NAME = 'navigation-toggle-state';
  static readonly EXPANDED_LIST_CACHE_NAME = 'navigation-expanded-list';
  static readonly LAST_MAIN_ROUTE_CACHE_NAME = 'last-main-route';

  private readonly _isMobileMenuOpen = signal<boolean>(false);
  private readonly _isSidebarOpen = signal<boolean>(false);
  private readonly _isNavigationEnabled = signal<boolean>(true);
  private readonly _selectedListId = signal<string | null>(null);
  private readonly _selectedItem = signal<Record<string, any> | null>(null);
  private readonly _isSettingsOpen = signal<boolean>(false);
  private readonly _lastMainRoute = signal<string | null>(null);

  private _seasonSetupNavigation: INavItem = {
    route: '/account/season-setup',
    label: 'Season Setup',
    icon: 'assets/icons/general/system-feedback-launch.svg',
    permission: PermissionEnum.PagesMasterfiles,
    alert: true,
  };

  private readonly _currentUrl = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map((event: NavigationEnd) => event.urlAfterRedirects.split('?')[0]),
    ),
  );

  public readonly isMobileMenuOpen = this._isMobileMenuOpen.asReadonly();
  public readonly isSidebarOpen = this._isSidebarOpen.asReadonly();
  public readonly isNavigationEnabled = this._isNavigationEnabled.asReadonly();
  public readonly selectedListId = this._selectedListId.asReadonly();
  public readonly selectedItem = this._selectedItem.asReadonly();
  public readonly isSettingsOpen = this._isSettingsOpen.asReadonly();
  public readonly lastMainRoute = this._lastMainRoute.asReadonly();

  private _mainNavigation: INavigationType[] = [
    {
      route: '/account/dashboard',
      label: 'Dashboard',
      icon: 'assets/icons/general/map.svg',
      permission: PermissionEnum.PagesDashboard,
    },
    {
      label: 'Trade',
      icon: 'assets/icons/general/arrow-up-down.svg',
      permissions: [PermissionEnum.PagesIncotermCalculator],
      items: [
        {
          route: '/account/incoterm-calculator',
          label: 'Incoterm Calculator',
          permission: PermissionEnum.PagesIncotermCalculator,
          state: 'beta',
        },
        {
          route: '/account/offers',
          label: 'Offers',
          permission: PermissionEnum.PagesIncotermCalculator,
          state: 'alpha',
        },
      ],
    },
    {
      label: 'Plan',
      icon: 'assets/icons/general/box-empty.svg',
      permissions: [
        PermissionEnum.PagesEstimations,
        PermissionEnum.PagesNewOrder,
        PermissionEnum.PagesOrders,
        PermissionEnum.PagesPlanning,
        PermissionEnum.PagesPackInstructions,
      ],
      items: [
        {
          route: '/account/estimations',
          label: 'Estimations',
          permission: PermissionEnum.PagesEstimations,
        },
        {
          route: '/account/orders',
          label: 'Orders',
          permission: PermissionEnum.PagesOrders,
        },
        {
          route: '/account/planning',
          label: 'Planning',
          permission: PermissionEnum.PagesPlanning,
        },
        {
          route: '/account/pack-instructions',
          label: 'Pack Instructions',
          permission: PermissionEnum.PagesPackInstructions,
        },
      ],
    },
    {
      route: '/account/stock',
      label: 'Stock',
      icon: './assets/icons/dashboard/cards/pallet.svg',
      permission: PermissionEnum.PagesStock,
    },
    {
      label: 'Move',
      icon: 'assets/icons/general/location-movement.svg',
      permissions: [
        PermissionEnum.PagesLoadOutInstructions,
        PermissionEnum.PagesCommercialInvoices,
        PermissionEnum.PagesShipments,
        PermissionEnum.PagesAirFreightShipments,
      ],
      items: [
        {
          route: '/account/load-out-instructions',
          label: 'Load Out Instructions',
          permission: PermissionEnum.PagesLoadOutInstructions,
        },
        {
          route: '/account/commercial-invoices',
          label: 'Commercial Invoices',
          permission: PermissionEnum.PagesCommercialInvoices,
        },
        {
          route: '/account/shipments',
          label: 'Shipments',
          permission: PermissionEnum.PagesShipments,
        },
        {
          route: '/account/air-freight-shipments',
          label: 'Air Freight Shipments',
          permission: PermissionEnum.PagesAirFreightShipments,
        },
      ],
    },
    {
      label: 'Quality',
      icon: '/assets/icons/general/shield-check.svg',
      permissions: [PermissionEnum.PagesClaims],
      items: [
        {
          label: 'Arrival QC',
          route: '/account/arrival-qc',
          permission: PermissionEnum.PagesClaims,
          state: 'beta',
        },
      ],
    },
    {
      label: 'Transact',
      icon: 'assets/icons/general/bank-card.svg',
      permissions: [
        PermissionEnum.PagesCashFlow,
        PermissionEnum.PagesCommercialTerms,
        PermissionEnum.PagesFinancePipeline,
        PermissionEnum.PagesPayments,
        PermissionEnum.PagesFinanceIndex,
        PermissionEnum.PagesFinanceRuleManagement,
        PermissionEnum.PagesDefaultCosts,
        PermissionEnum.PagesProfitAndLoss,
      ],
      items: [
        {
          label: 'Cash Flow',
          route: '/account/cash-flow',
          permission: PermissionEnum.PagesCashFlow,
        },
        {
          label: 'Customer Invoices',
          route: '/account/financial-invoices',
          permission: PermissionEnum.PagesCustomerInvoices,
        },
        {
          label: 'Vendor Invoices',
          route: '/account/vendor-invoices',
          permission: PermissionEnum.PagesVendorInvoices,
        },
        {
          label: 'Default Costs',
          route: '/account/default-costs',
          permission: PermissionEnum.PagesDefaultCosts,
        },
        {
          label: 'Payments',
          route: '/account/payments',
          permission: PermissionEnum.PagesPayments,
          state: 'beta',
        },
        {
          label: 'Reports',
          route: '/account/finance/reports',
          permission: PermissionEnum.PagesCashFlow, // TODO: add permission
        },
        {
          label: 'Profit & Loss',
          route: '/account/finance/profit-and-loss',
          permission: PermissionEnum.PagesProfitAndLoss,
          state: 'beta',
        },
        {
          label: 'Commercial Terms',
          route: '/account/commercial-terms',
          permission: PermissionEnum.PagesCommercialTerms,
        },
        {
          label: 'Finance Rules',
          route: '/account/finance-rules',
          permission: PermissionEnum.PagesFinanceRuleManagement,
          state: 'beta',
        },
        {
          label: 'Finance Pipeline',
          route: '/account/finance/pipeline',
          permission: PermissionEnum.PagesFinancePipeline,
          state: 'beta',
        },
      ],
    },
    {
      label: 'Insights',
      icon: 'assets/icons/general/chart.svg',
      route: '/account/insights',
      permission: PermissionEnum.PagesInsights,
    },
  ];

  private _settingsNavigation: INavigationType[] = [
    // company
    {
      categoryLabel: 'Company',
      categoryItems: [
        {
          label: 'Company profile',
          route: '/account/company',
          permission: PermissionEnum.PagesCompany,
          icon: 'assets/icons/general/users.svg',
        },
      ],
    },

    // network
    {
      categoryLabel: 'Network',
      categoryItems: [
        {
          label: 'Trade Partners',
          route: '/account/partners',
          permission: PermissionEnum.PagesPartners,
          icon: 'assets/icons/general/partners.svg',
        },
      ],
    },

    // masterfiles
    {
      categoryLabel: 'Masterfiles',
      categoryItems: [
        {
          label: 'Production',
          icon: 'assets/icons/general/farm-tractor.svg',
          permissions: [
            PermissionEnum.MasterfilesFarmVarieties,
            PermissionEnum.MasterfilesOrchards,
            PermissionEnum.MasterfilesFoodBusinessOperators,
            PermissionEnum.MasterfilesCartonConversions,
          ],
          items: [
            {
              route: '/account/masterfiles/farm-varieties',
              label: 'Farm Varieties',
              permission: PermissionEnum.MasterfilesFarmVarieties,
            },
            {
              route: '/account/masterfiles/orchards',
              label: 'Orchards',
              permission: PermissionEnum.MasterfilesOrchards,
            },
            {
              route: '/account/masterfiles/food-business-operators',
              label: 'Food Business Operators',
              permission: PermissionEnum.MasterfilesFoodBusinessOperators,
            },
            {
              route: '/account/masterfiles/carton-conversions',
              label: 'Carton Conversions',
              permission: PermissionEnum.MasterfilesCartonConversions,
            },
          ],
        },
        {
          label: 'Integrations',
          icon: 'assets/icons/general/categories-channels.svg',
          permissions: [
            PermissionEnum.PagesMasterfiles,
            PermissionEnum.MasterfilesPackhouses,
          ],
          items: [
            {
              route: '/account/masterfiles/packhouse-manager',
              label: 'Packhouse Manager',
              permission: PermissionEnum.MasterfilesPackhouses,
            },
            {
              route: '/account/masterfiles/supply-chain-setup',
              label: 'Supply Chain Setups',
              permission: PermissionEnum.PagesMasterfiles,
            },
          ],
        },
        {
          label: 'Error manager',
          route: '/account/masterfiles/errors',
          permission: PermissionEnum.MasterfilesErrors,
          icon: 'assets/icons/general/system-feedback-warning.svg',
        },
      ],
    },

    {
      categoryLabel: 'Personal settings',
      categoryItems: [
        {
          label: 'My profile',
          route: '/account/settings',
          permission: PermissionEnum.PagesSettings,
          icon: 'assets/icons/general/user.svg',
        },
        {
          label: 'Notification preferences',
          icon: 'assets/icons/general/social-bell.svg',
          permissions: [PermissionEnum.PagesSettings],
          items: [
            {
              label: 'General preferences',
              route: '/account/notification-preferences/general-preferences',
              permission: PermissionEnum.PagesSettings,
            },
            {
              label: 'Workflow',
              route: '/account/notification-preferences/workflow',
              permission: PermissionEnum.PagesDemo,
            },
            {
              label: 'Data & tracking',
              route: '/account/notification-preferences/data-and-tracking',
              permission: PermissionEnum.PagesDemo,
            },
            {
              label: 'Team collaboration',
              route: '/account/notification-preferences/team-collaboration',
              permission: PermissionEnum.PagesDemo,
            },
            {
              label: 'Trade network',
              route: '/account/notification-preferences/trade-network',
              permission: PermissionEnum.PagesIncotermCalculator,
              state: 'beta',
            },
            {
              label: 'Compliance',
              route: '/account/notification-preferences/compliance',
              permission: PermissionEnum.PagesDemo,
            },
          ],
        },
      ],
    },
  ];

  hasSeasonNavigation: boolean = false;

  constructor() {
    this.initializeState();
    this.watchRoute();
  }

  /**
   * Adds the season setup navigation to the beginning of the main navigation. This is used to show the season setup navigation in the sidebar.
   */
  public showSeasonSetupNavigation() {
    if (this.hasSeasonNavigation) {
      return;
    }

    // add the season setup navigation to the beginning of the main navigation
    this._mainNavigation = [
      this._seasonSetupNavigation,
      ...this._mainNavigation,
    ];
    this.hasSeasonNavigation = true;
  }

  /**
   * Removes the season setup navigation from the main navigation. This is used to hide the season setup navigation in the sidebar.
   */
  public hideSeasonSetupNavigation() {
    if (!this.hasSeasonNavigation) {
      return;
    }

    // remove the season setup navigation from the main navigation
    this._mainNavigation = this._mainNavigation.filter(
      (nav: INavigationType) => {
        if (this.isNavItem(nav)) {
          return nav.route !== '/account/season-setup';
        }
        return true;
      },
    );
    this.hasSeasonNavigation = false;
  }

  /** Watches the route and updates the navigation state. */
  private watchRoute(): void {
    effect(
      () => {
        const urlPath = this._currentUrl();

        if (urlPath) {
          this.updateSettingsState(urlPath);
          this.updateLastMainRoute(urlPath);
        }
      },
      { injector: this.injector },
    );
  }

  private updateSettingsState(urlPath: string): void {
    if (this.isRouteInNavigation(urlPath, this._settingsNavigation)) {
      this.openSettings();
    } else {
      this.closeSettings();
    }
  }

  private updateLastMainRoute(urlPath: string): void {
    const isSettingsRoute = this.isRouteInNavigation(
      urlPath,
      this._settingsNavigation,
    );
    const isMainRoute = this.isRouteInNavigation(urlPath, this._mainNavigation);

    if (!isSettingsRoute && isMainRoute) {
      this._lastMainRoute.set(urlPath);
      this.setLocalStorage(urlPath, NavService.LAST_MAIN_ROUTE_CACHE_NAME);
    }
  }

  /** Initializes the navigation state from local storage. */
  private initializeState(): void {
    const toggleState = this.getLocalStorage(
      NavService.TOGGLE_STATE_CACHE_NAME,
    );
    if (toggleState === false) {
      this.closeSidebar();
    } else {
      this.openSidebar();
    }

    const expandedList = this.getLocalStorage(
      NavService.EXPANDED_LIST_CACHE_NAME,
    );
    if (expandedList) {
      this._selectedListId.set(expandedList);
    }

    const lastMainRoute = this.getLocalStorage(
      NavService.LAST_MAIN_ROUTE_CACHE_NAME,
    );
    if (lastMainRoute) {
      this._lastMainRoute.set(lastMainRoute);
    }
  }

  /** Sets a value in local storage. */
  public setLocalStorage(value: any, name: string): void {
    localStorage.setItem(name, JSON.stringify(value));
  }

  /** Gets a value from local storage. */
  public getLocalStorage(name: string): any | null {
    const value = localStorage.getItem(name);
    return value ? JSON.parse(value) : null;
  }

  /** Toggles the mobile menu state. */
  public toggleMobileMenu(): void {
    if (!this._isNavigationEnabled()) {
      return;
    }

    this._isMobileMenuOpen.update((isOpen) => !isOpen);
  }

  /** Closes the mobile menu. */
  public closeMobileMenu(): void {
    this._isMobileMenuOpen.set(false);
  }

  /** Toggles the sidebar state. */
  public toggleSidebar(): void {
    if (!this._isNavigationEnabled()) {
      return;
    }

    this._isSidebarOpen.update((isOpen) => !isOpen);

    this.setLocalStorage(
      this._isSidebarOpen(),
      NavService.TOGGLE_STATE_CACHE_NAME,
    );
    this.forceResize();
  }

  /** Opens the sidebar. */
  public openSidebar(): void {
    if (!this._isNavigationEnabled()) {
      return;
    }

    this._isSidebarOpen.set(true);
    this.setLocalStorage(
      this._isSidebarOpen(),
      NavService.TOGGLE_STATE_CACHE_NAME,
    );
    this.forceResize();
  }

  /** Closes the sidebar. */
  public closeSidebar(): void {
    if (!this._isNavigationEnabled()) {
      return;
    }

    this._isSidebarOpen.set(false);
    this.setLocalStorage(
      this._isSidebarOpen(),
      NavService.TOGGLE_STATE_CACHE_NAME,
    );
    this.forceResize();
  }

  /** Enables navigation */
  public enableNavigation(): void {
    this._isNavigationEnabled.set(true);
    this.initializeState();
  }

  /** Disables navigation and automatically closes all dependent states. */
  public disableNavigation(): void {
    this._isNavigationEnabled.set(false);

    // Automatically close all dependent states
    this._isMobileMenuOpen.set(false);
    this._isSidebarOpen.set(false);
  }

  /** Selects a list by id. */
  /** Essentially expands/ collapses a list of navigation items. */
  public selectList(list: INavList | null): void {
    if (!this._isNavigationEnabled()) {
      return;
    }

    const selectedList: string | null = list?.label ?? null;
    // Toggle selected list id if it is already selected
    if (selectedList === this._selectedListId()) {
      this._selectedListId.set(null);
      this.setLocalStorage(null, NavService.EXPANDED_LIST_CACHE_NAME);
      return;
    }

    this._selectedListId.set(selectedList);
    this.setLocalStorage(selectedList, NavService.EXPANDED_LIST_CACHE_NAME);
  }

  /** Selects an item by id. */
  /** Essentially selects a navigation item. */
  public selectItem(item: INavItem | null): void {
    if (!this._isNavigationEnabled()) {
      return;
    }

    this._selectedItem.set(item);
  }

  /** Checks if an item is selected. */
  public isSelectedItem(item: Record<string, any> | null): boolean {
    return this.selectedItem() === item;
  }

  /** Checks if a list is selected. */
  public isSelectedList(listId: string | null): boolean {
    return this.selectedListId() === listId;
  }

  private isRouteInNavigation(
    urlPath: string,
    navigation: INavigationType[],
  ): boolean {
    return navigation.some((nav) => {
      if (this.isCategory(nav)) {
        return this.isRouteInNavigation(urlPath, nav.categoryItems);
      }

      if (this.isNavList(nav)) {
        return nav.items.some(
          (item) => this.isNavItem(item) && urlPath.startsWith(item.route),
        );
      }

      if (this.isNavItem(nav)) {
        return urlPath.startsWith(nav.route);
      }

      return false;
    });
  }

  /** Resets all navigation state to defaults. */
  public resetState(): void {
    this._isNavigationEnabled.set(true);
    this._isMobileMenuOpen.set(false);
    this._isSidebarOpen.set(false);
    this._selectedListId.set(null);
    this._selectedItem.set(null);
  }

  /** Forces a resize event to ensure that any libraries or features
   * that rely on the window size are updated. */
  private forceResize(): void {
    window.dispatchEvent(new Event('resize'));
  }

  /** Toggles the settings state. */
  toggleSettings(): void {
    this._isSettingsOpen.update((isOpen) => !isOpen);
  }

  /** Closes the settings state. */
  closeSettings(): void {
    this._isSettingsOpen.set(false);
  }

  /** Opens the settings state. */
  openSettings(): void {
    this._isSettingsOpen.set(true);
  }

  /** Gets the main navigation. */
  getMainNavigation(): INavigationType[] {
    return this._transformNavigationForView(this._mainNavigation);
  }

  /** Gets the settings navigation. */
  getSettingsNavigation(): INavigationType[] {
    return this._transformNavigationForView(this._settingsNavigation);
  }

  /** Checks if the current route is a settings page. */
  isSettingsPage(): boolean {
    const urlPath: string = this.router.url.split('?')[0];
    return this.isRouteInNavigation(urlPath, this._settingsNavigation);
  }

  /** Navigates to the settings page. */
  navigateToSettings(): void {
    this.openSidebar();
    this.openSettings();
    this.router.navigate(['/account/company']);
  }

  /** Navigates to the my profile page. */
  navigateToMyProfile(): void {
    this.openSidebar();
    this.openSettings();
    this.router.navigate(['/account/settings']);
  }

  /** Navigates to the support page. */
  navigateToSupport(): void {
    this.openSidebar();
    this.openSettings();
    this.router.navigate(['/account/support']);
  }

  isCategory(nav: INavigationType): nav is INavCategory {
    return 'categoryLabel' in nav;
  }

  isNavList(nav: INavigationType): nav is INavList {
    return 'items' in nav;
  }

  isNavItem(nav: INavigationType): nav is INavItem {
    return 'route' in nav;
  }

  private _getCategoryPermissions(category: INavCategory): string[] {
    const permissions: string[] = [];

    category.categoryItems.forEach((item: INavItem | INavList) => {
      if (this.isNavList(item)) {
        permissions.push(...item.permissions);
      } else {
        permissions.push(item.permission);
      }
    });

    return [...new Set(permissions)];
  }

  /**
   * Dynamically calculates the permissions for the entire category
   * and returns the navigation with the category permissions.
   * This is used to simplify category rendering
   */
  private _transformNavigationForView(
    navigation: INavigationType[],
  ): INavigationType[] {
    return navigation.map((nav) => {
      if (this.isCategory(nav)) {
        return {
          ...nav,
          categoryPermissions: this._getCategoryPermissions(nav),
        };
      }
      return nav;
    });
  }
}
